import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdSearch, MdCheckCircle, MdCancel, MdAccountBalanceWallet, MdDelete, MdVisibility } from 'react-icons/md';
import ReasonModal from '../components/ReasonModal';
import { formatDate, formatINR, statusBadgeClass } from '../utils/format';

const TABS = ['pending', 'approved', 'rejected', 'all'];

export default function Contributors() {
  const location = useLocation();
  const [tab, setTab] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reasons, setReasons] = useState([]);

  const [detail, setDetail] = useState(null);   // full contributor (+payout)
  const [wallet, setWallet] = useState(null);    // { stats, ledger }
  const [rejectFor, setRejectFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab !== 'all') params.status = tab;
      if (search) params.search = search;
      const res = await api.get('/admin/contributors', { params });
      setList(res.data.data || []);
    } catch {
      toast.error('Failed to load contributors');
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Read URL search/id parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchFromUrl = searchParams.get('search') || searchParams.get('q') || '';
    const idFromUrl = searchParams.get('id');

    if (searchFromUrl || idFromUrl) {
      setTab('all');
      if (searchFromUrl) setSearch(searchFromUrl);
      if (idFromUrl) openDetail(idFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    api.get('/admin/contributors/meta/rejection-reasons')
      .then((res) => setReasons(res.data.data?.contributor || []))
      .catch(() => {});
  }, []);

  const openDetail = async (id) => {
    setDetail('loading');
    setWallet(null);
    try {
      const [c, w] = await Promise.all([
        api.get(`/admin/contributors/${id}`),
        api.get(`/admin/contributors/${id}/wallet`).catch(() => null),
      ]);
      setDetail(c.data.data);
      if (w) setWallet(w.data.data);
    } catch {
      toast.error('Failed to load details');
      setDetail(null);
    }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributors/${id}/approve`);
      toast.success('Contributor approved');
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const reject = async (reason) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributors/${rejectFor.id}/reject`, { reason });
      toast.success('Contributor rejected');
      setRejectFor(null);
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    setBusy(true);
    try {
      await api.delete(`/admin/contributors/${deleteFor.id}`);
      toast.success('Contributor deleted successfully.');
      setDeleteFor(null);
      if (detail?.id === deleteFor.id) setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete contributor');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Contributors</h2>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="filter-bar">
        <div className="search-input-wrap">
          <MdSearch className="search-icon" />
          <input
            className="search-input"
            placeholder="Search name, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state">
          <MdCheckCircle className="empty-icon" />
          <h3>No contributors</h3>
          <p>Nothing in “{tab}”.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Phone</th><th>Location</th><th>Status</th><th style={{ textAlign: 'right', paddingRight: '24px' }}>Actions</th></tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.email}</td>
                  <td>{c.phone}</td>
                  <td>{[c.city, c.state].filter(Boolean).join(', ') || '-'}</td>
                  <td><span className={`badge ${statusBadgeClass(c.status)}`}>{c.status}</span></td>
                  <td style={{ textAlign: 'right', paddingRight: '24px' }}>
                    <div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => openDetail(c.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', border: '1px solid #CBD5E1', background: '#FFF', color: '#334155', cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        <MdVisibility size={16} /> View
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setDeleteFor(c)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#EF233C', color: '#FFF', border: 'none', cursor: 'pointer', boxShadow: '0 2px 4px rgba(239, 35, 60, 0.2)', transition: 'all 0.2s ease' }}
                      >
                        <MdDelete size={16} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {detail && detail !== 'loading' && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{detail.name} <span className={`badge ${statusBadgeClass(detail.status)}`}>{detail.status}</span></h3>
              <button className="modal-close" onClick={() => setDetail(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="detail-row"><span className="detail-label">Email</span><span>{detail.email}</span></div>
              <div className="detail-row"><span className="detail-label">Phone</span><span>{detail.phone}</span></div>
              <div className="detail-row"><span className="detail-label">Category of interest</span><span>{detail.category_interest || '-'}</span></div>
              <div className="detail-row"><span className="detail-label">State / City</span><span>{[detail.city, detail.state].filter(Boolean).join(', ') || '-'}</span></div>
              <div className="detail-row"><span className="detail-label">Registered</span><span>{formatDate(detail.created_at)}</span></div>

              <div className="detail-row">
                <span className="detail-label">Police case?</span>
                <span>{detail.has_police_case ? `Yes — ${detail.police_case_details || ''}` : 'No'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Written elsewhere?</span>
                <span>{detail.has_prior_writing ? `Yes — ${detail.prior_writing_details || ''}` : 'No'}</span>
              </div>
              <div className="detail-row"><span className="detail-label">Terms accepted</span><span>{detail.terms_accepted ? 'Yes' : 'No'}</span></div>
              <div className="detail-row"><span className="detail-label">Submissions</span><span>{detail.submission_count ?? 0}</span></div>

              {detail.status === 'rejected' && detail.rejection_reason && (
                <div className="detail-row"><span className="detail-label">Rejection reason</span><span>{detail.rejection_reason}</span></div>
              )}

              {/* Wallet snapshot */}
              {wallet?.stats && (
                <div className="card" style={{ marginTop: 16, background: 'var(--bg)' }}>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <MdAccountBalanceWallet /> Wallet
                  </h4>
                  <div className="detail-row"><span className="detail-label">Income till date</span><span>{formatINR(wallet.stats.income_till_date)}</span></div>
                  <div className="detail-row"><span className="detail-label">Available balance</span><span>{formatINR(wallet.stats.available_balance)}</span></div>
                  <div className="detail-row"><span className="detail-label">Accepted articles</span><span>{wallet.stats.accepted}</span></div>
                </div>
              )}
            </div>

            {detail.status === 'pending' && (
              <div className="modal-footer">
                <button className="btn btn-danger" disabled={busy} onClick={() => setRejectFor(detail)}>
                  <MdCancel /> Reject
                </button>
                <button className="btn btn-primary" disabled={busy} onClick={() => approve(detail.id)}>
                  <MdCheckCircle /> Approve
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {rejectFor && (
        <ReasonModal
          title={`Reject ${rejectFor.name}`}
          reasons={reasons}
          confirmLabel="Reject Contributor"
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onConfirm={reject}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteFor && (
        <div className="modal-overlay" onClick={() => setDeleteFor(null)} style={{ zIndex: 1100, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} style={{ padding: 24, borderRadius: 16, background: '#FFF', maxWidth: 440, width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #E2E8F0', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#EF233C', fontWeight: 800 }}>Delete Contributor</h3>
              <button className="modal-close" onClick={() => setDeleteFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><MdClose size={20} /></button>
            </div>
            <p style={{ fontSize: 14, color: '#475569', marginBottom: 14, lineHeight: 1.5 }}>
              Are you sure you want to delete this contributor?
            </p>
            <div style={{ padding: 14, background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#64748B', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Contributor Name:</div>
              <div style={{ fontSize: 16, color: '#0F172A', fontWeight: 800 }}>{deleteFor.name}</div>
              {deleteFor.email && <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>{deleteFor.email}</div>}
            </div>
            <p style={{ fontSize: 12.5, color: '#DC2626', fontWeight: 700, marginBottom: 20 }}>
              ⚠️ This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteFor(null)} disabled={busy} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FFF', cursor: 'pointer' }}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={busy} style={{ background: '#EF233C', color: '#FFF', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
